import React, { useState, useEffect, useContext } from "react"
import { navigate as gatsbyNavigate } from "gatsby"
import { getTranslatedPath } from "../../linkUtils"
import "./overview.css"
import { FirebaseContext } from "../Firebase"
import useDashboardRegionKeyboardNavigation from "../hooks/useDashboardRegionKeyboardNavigation"
import dayjs from "dayjs"
import questionData from "../../components/assessment/questions.js"
import { FormattedMessage } from "react-intl"
import { DotLottieReact } from "@lottiefiles/dotlottie-react"
import {
  WingsEyebrow,
  WingsHeading,
  WingsText,
  WingsButton,
  WingsDetailText,
} from "../../global-styles/wingspans-ui/components"

/**
 * AssessmentOverview Component
 *
 * Displays the TAL-E Experience Translator and Interest Profiler cards.
 * Each card shows the assessment illustration, description, and context-aware
 * action buttons based on whether the user has completed the assessment.
 *
 * @component
 */
const AssessmentOverview = () => {
  const { profile, firebase } = useContext(FirebaseContext)

  const [isAssessmentTaken, setIsAssessmentTaken] = useState(false)
  const [assessmentDate, setAssessmentDate] = useState(null)
  const [taleSubmissionDate, setTaleSubmissionDate] = useState(null)
  const [canResume, setCanResume] = useState(false)
  const [isTaleTaken, setIsTaleTaken] = useState(false)

  const navigateToLocalizedPath = (to) => {
    if (typeof window === "undefined") return
    const localizedPath = getTranslatedPath(window.location.pathname || "", to)
    gatsbyNavigate(localizedPath)
  }

  useDashboardRegionKeyboardNavigation({
    isEnabled: true,
    regionOrder: ["assessmentCards"],
    focusableSelectorsByRegion: {
      assessmentCards: ".assessments-overview-card-actions button, .assessments-overview-card-actions a[href]",
    },
    shouldWrapOnBoundary: () => true,
  })

  useEffect(() => {
    if (!firebase) return

    const fetchSavedResponses = async () => {
      try {
        const savedResponses = await firebase.getUserTaleResponsesAndMatches()
        const { data } = savedResponses ?? {}
        const { taleResponses } = data ?? {}

        if (taleResponses) {
          const { isSubmitted, submittedAt } = taleResponses
          const submissionDate = dayjs(new Date(submittedAt?._seconds * 1000)).format("MMMM DD, YYYY") ?? null

          if (isSubmitted === true) {
            setIsTaleTaken(true)
            setTaleSubmissionDate(submissionDate)
          } else if (isSubmitted === false) {
            setIsTaleTaken(false)
          }
        }
      } catch (err) {
        console.error("Error fetching TAL-E responses:", err)
      }
    }

    fetchSavedResponses()
  }, [firebase])

  useEffect(() => {
    if (!profile?.assessmentRecord) return

    const { createdDate, responsesVersion, responses, resultProfile } = profile.assessmentRecord

    if (responsesVersion === questionData.version && responses.length < questionData.questions.length) {
      setCanResume(true)
    }

    if (resultProfile) {
      setIsAssessmentTaken(true)
    }

    if (createdDate) {
      setAssessmentDate(dayjs(new Date(createdDate.toDate())).format("MMMM DD, YYYY"))
    }
  }, [profile])

  return (
    <div className="assessments-overview w-container">
      <div className="assessments-overview-header w-mb-2">
        <WingsEyebrow as="p">
          <FormattedMessage id="assessment-overview-take-an" defaultMessage="TAKE AN" />
        </WingsEyebrow>
        <WingsHeading level={1}>
          <FormattedMessage id="assessment-overview-content-heading" defaultMessage="Assessment" />
        </WingsHeading>
      </div>

      <div
        className="assessments-overview-cards w-flex w-justify-evenly w-flex-wrap w-mb-4"
        data-keyboard-region="assessmentCards"
      >
        {/* TAL-E Experience Translator */}
        <div className="assessments-overview-card">
          <div className="assessments-overview-card-illustration">
            <DotLottieReact
              src="/assets/animations/lottie/TAL-E_Irregular_Large.lottie"
              speed={1}
              autoplay
              loop
              style={{ width: "100%", height: "100%", maxWidth: "500px", maxHeight: "500px" }}
            />
          </div>

          <WingsEyebrow as="p" className="w-mb-1">
            <FormattedMessage id="assessment-overview-tale-assessment" defaultMessage="TAL-E Experience Translator" />
          </WingsEyebrow>

          <WingsText className="w-mb-2">
            <FormattedMessage
              id="assessment-overview-tale-assessment-description"
              defaultMessage="Together, we'll discover transferable skills and career possibilities based on your real world experiences and my AI-powered insights."
            />
          </WingsText>

          <div className="assessments-overview-card-actions w-flex w-flex-column w-items-start w-gap-2">
            {!isTaleTaken ? (
              <WingsButton variant="tal-e" size="small" onClick={() => navigateToLocalizedPath("/tal-e/")}>
                <FormattedMessage id="tale-assessment-overview-take-assessment" defaultMessage="Take Assessment" />
              </WingsButton>
            ) : (
              <div className="w-flex w-gap-1">
                <WingsButton variant="tal-e" size="small" onClick={() => navigateToLocalizedPath("/tal-e/?page=results")}>
                  <FormattedMessage id="tale-assessment-overview-review-assessment-results" defaultMessage="Review Results" />
                </WingsButton>
                <WingsButton variant="tal-e-outline" size="small" onClick={() => navigateToLocalizedPath("/tal-e/")}>
                  <FormattedMessage id="tale-assessment-overview-retake-assessment" defaultMessage="Retake Assessment" />
                </WingsButton>
              </div>
            )}

            {isTaleTaken && taleSubmissionDate && (
              <WingsDetailText>
                <FormattedMessage id="tale-assessment-overview-completed" defaultMessage="Completed" />: {taleSubmissionDate}
              </WingsDetailText>
            )}
          </div>
        </div>

        {/* Interest Profiler */}
        <div className="assessments-overview-card">
          <div className="assessments-overview-card-illustration">
            <img
              className="assessments-overview-card-img"
              src="https://firebasestorage.googleapis.com/v0/b/lucid-bond-288217.appspot.com/o/assessment-assets%2FUntitled%20design.png?alt=media&token=b3ca7241-c114-469b-9881-65397f8a0045"
              alt=""
            />
          </div>

          <WingsEyebrow as="p" className="w-mb-1">
            <FormattedMessage id="assessment-overview-personality-assessment" defaultMessage="Interest Profiler" />
          </WingsEyebrow>

          <WingsText className="w-mb-2">
            <FormattedMessage
              id="assessment-overview-personality-assessment-description"
              defaultMessage="The world is big and full of opportunity. This assessment uses the Holland Code to match you to career possibilities and programs that are right for you."
            />
          </WingsText>

          <div className="assessments-overview-card-actions w-flex w-flex-column w-items-start w-gap-2">
            {!isAssessmentTaken ? (
              <WingsButton variant="tal-e" size="small" onClick={() => navigateToLocalizedPath("/assessment/personality")}>
                {canResume ? (
                  <FormattedMessage id="assessment-overview-resume-assessment" defaultMessage="Resume Assessment" />
                ) : (
                  <FormattedMessage id="assessment-overview-take-personality-assessment" defaultMessage="Interest Profiler" />
                )}
              </WingsButton>
            ) : (
              <div className="w-flex w-gap-1">
                <WingsButton variant="tal-e" size="small" onClick={() => navigateToLocalizedPath("/assessment/personality/results")}>
                  <FormattedMessage id="assessment-overview-review-results" defaultMessage="Review Results" />
                </WingsButton>
                <WingsButton variant="tal-e-outline" size="small" onClick={() => navigateToLocalizedPath("/assessment/personality")}>
                  <FormattedMessage id="assessment-overview-retake-assessment" defaultMessage="Retake Assessment" />
                </WingsButton>
              </div>
            )}

            {isAssessmentTaken && assessmentDate && (
              <WingsDetailText>
                <FormattedMessage id="assessment-overview-completed" defaultMessage="Completed" />: {assessmentDate}
              </WingsDetailText>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default AssessmentOverview
